import cx from 'classnames';
import moment from 'moment';
import Numeral from 'numeral';
import { ReactElement } from 'react';
import { Trans } from 'react-i18next';
import { formatPhaseDisplay, getPhaseProgress } from '../../../../../../../shared/common/bossPhases';
import { getBestPrecision } from '../../../../../../../shared/common/utils';
import { plural, relativeDate } from '../../../../../../../shared/frontend/libs/utils';
import { asset } from '../../../../../../../shared/frontend/services/assetResolverService';
import { namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { ComplexTooltip } from '../../../../../../../shared/frontend/components/presentational/tooltip';
import { Stack } from '../../../../../../../shared/frontend/components/stack/Stack';
import { DarkModeIconTooltip } from '../../../../../../../shared/frontend/components/darkModeIconTooltip';
import { Typography } from '../../../../../../../shared/frontend/components/typography';
import type { TCurrentAttemptProgressProps } from './types';

export function CurrentAttemptProgress(props: TCurrentAttemptProgressProps) {
  const latestAttempt = props.attempts?.sort((a, b) => b.lastPulledAt?.localeCompare(a.lastPulledAt))?.[0];
  if (!latestAttempt) {
    return null;
  }

  const rawPercentValue =
    latestAttempt.overallPercent !== null ? `${getBestPrecision(100 * latestAttempt.overallPercent)}%` : '-';
  const phaseProgress =
    latestAttempt.overallPercent !== null
      ? getPhaseProgress(props.difficulty, latestAttempt.slug, {
          overallPercent: latestAttempt.overallPercent,
          elapsedMs: latestAttempt.encounter?.durationMs
        })
      : null;
  const phaseAwarePercentValue = phaseProgress
    ? formatPhaseDisplay(phaseProgress, (bossPercent) => `${getBestPrecision(bossPercent * 100)}%`)
    : latestAttempt.progressDisplay || null;
  const showsPhaseAwareProgress = !!phaseAwarePercentValue;
  let percentValue = phaseAwarePercentValue || rawPercentValue;
  let percent = (
    <span
      className={cx(
        'rio-realm-link',
        props.size === 'small' ? 'slds-m-right--x-small' : '',
        latestAttempt.overallPercent === null ? 'slds-m-left--xx-small' : ''
      )}
    >
      {latestAttempt.overallPercent === null ? <i className="fa fa-eye-slash" /> : percentValue}
    </span>
  );

  const tooltipComponents: { best?: ReactElement; pulls?: ReactElement } = {};

  if (latestAttempt.overallPercent !== null) {
    tooltipComponents.best = <>{percentValue} Best</>;
  }
  if (latestAttempt.pullCount !== null) {
    tooltipComponents.pulls = (
      <>
        {latestAttempt.pullCount} {plural('Pull', latestAttempt.pullCount)}
      </>
    );
  }
  if (showsPhaseAwareProgress) {
    percent = (
      <span className={cx('rio-realm-link text-muted', props.size === 'small' ? 'slds-m-right--x-small' : '')}>
        {percentValue}
      </span>
    );
  }

  const tooltipContent = (
    <>
      {tooltipComponents.best}
      {tooltipComponents.pulls && tooltipComponents.best && ' • '}
      {tooltipComponents.pulls}
    </>
  );

  const bossIconUrl = asset(latestAttempt.iconUrl);
  const icon = (
    <span
      className={cx(
        'slds-avatar rio-border--light',
        props.size === 'small' ? 'slds-avatar--x-small' : 'slds-avatar--small'
      )}
      title={props.raid.name}
    >
      <img src={bossIconUrl} alt={latestAttempt.name} />
    </span>
  );

  if (props.bare) {
    if (latestAttempt.overallPercent === null) {
      return null;
    }

    return (
      <>
        <img src={bossIconUrl} className="raid-leaderboard-widget--pull-boss-icon" />
        <span className="raid-leaderboard-widget--pull-progress">{percentValue}</span>
      </>
    );
  }

  const shareRaidUntil = props.guildPrivacy?.shareRaidUntil;
  const isOnDarkMode = shareRaidUntil ? moment.utc(shareRaidUntil).isBefore(moment.utc()) : false;

  return (
    <Link
      className={props.className}
      to={resolveRoutePath(namedRoutes.GuildRaidComposition, {
        region: props.guild.region.slug,
        realm: props.guild.realm.slug,
        guild: props.guild.name,
        difficulty: props.difficulty,
        raid: props.raid.slug,
        boss: latestAttempt.slug
      })}
    >
      <Stack alignItems="center">
        <ComplexTooltip
          placement="top"
          tooltip={
            <div>
              <b>Latest Encounter Progress</b>
              <br />
              {props.raid.name} - {latestAttempt.name}
              <br />
              {tooltipContent}
              {latestAttempt.averageItemLevel && (
                <>
                  <br />
                  Item Level <b>{Numeral(latestAttempt.averageItemLevel).format('0.00')}</b>
                </>
              )}
              <br />
              <i>{relativeDate(latestAttempt.lastPulledAt)}</i>
              {isOnDarkMode && (
                <div className="slds-m-top--x-small">
                  <Typography.Text size="small" italic>
                    <Trans i18nKey="guilds:dark-mode.guild-in-dark-mode">
                      <i className="dark-mode-icon" />
                    </Trans>
                  </Typography.Text>
                </div>
              )}
            </div>
          }
        >
          {props.size === 'small' ? (
            <Stack
              className="slds-text-align--center"
              justifyContent="center"
              wrap
              alignItems="center"
              spacingInRems={2}
            >
              {percent}
              {icon}
            </Stack>
          ) : (
            <Stack
              className="slds-text-align--center"
              justifyContent="center"
              wrap
              alignItems="center"
              spacingInRems={3}
            >
              {icon}
              {percent}
            </Stack>
          )}
        </ComplexTooltip>

        {isOnDarkMode && <DarkModeIconTooltip shareRaidUntil={shareRaidUntil} />}
      </Stack>
    </Link>
  );
}
